import type {
  Extension,
  Identifier,
  Meta,
  Reference,
  Resource,
  ResourceType,
} from "@medplum/fhirtypes";
import type { ResourceRef } from "./fhir-resource-types";

export type ResourceWithIdentifierAndExtension = Resource & {
  identifier?: Identifier[];
  extension?: Extension[];
};

/**
 * Medplum-specific extensions to FHIR `Meta` used for multi-tenant access control.
 */
export type MedplumMeta = Meta & {
  account?: Reference | undefined;
  accounts?: Reference[] | undefined;
  compartment?: Reference[] | undefined;
};

/**
 * Formats a FHIR resource `versionId` as the `If-Match` header value for a version-aware
 * (optimistic-locking) update.
 *
 * FHIR ETags are **weak** — the format is `W/"<versionId>"` (the `W/` weak-validator prefix from
 * RFC 7232 §2.3, wrapping the quoted versionId). FHIR R4 specifies exactly this for concurrency
 * control: the server returns it in the `ETag` response header and expects the same value back in
 * `If-Match` on conditional updates. A bare versionId (no `W/"…"`) will not match.
 * See https://hl7.org/fhir/R4/http.html#concurrency (and Medplum, which follows it).
 *
 * `@medplum/core` does not expose a formatter for this — its client only forwards caller-set
 * headers — so this helper is the single source of truth for the format.
 */
export function weakETag(versionId: string): string {
  return `W/"${versionId}"`;
}

/**
 * Returns true when the value is a plain JSON object (Object or null prototype).
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Removes all undefined values from an object.
 */
export function cleanObject(obj: Record<string, any>): Record<string, any> {
  const cleanedObject: Record<string, any> = {};

  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleanedObject[key] = obj[key];
    }
  }

  return cleanedObject;
}

export function findIdentifier(
  resource: ResourceWithIdentifierAndExtension,
  system: string,
): Identifier | undefined {
  return resource.identifier?.find((identifier) => identifier.system === system);
}

/**
 * Converts an unknown reference-shaped input into a validated `ResourceRef`.
 */
export function toResourceRef({ ref }: { ref: unknown }): ResourceRef | undefined {
  if (!isPlainObject(ref)) return undefined;

  const reference = ref.reference;
  if (typeof reference !== "string" || reference.length === 0) return undefined;

  const display = ref.display;
  if (display !== undefined && typeof display !== "string") return undefined;

  const identifier = "identifier" in ref ? ref.identifier : undefined;
  const normalizedIdentifier = isPlainObject(identifier) ? (identifier as Identifier) : undefined;

  const type = "type" in ref ? ref.type : undefined;
  const normalizedType = typeof type === "string" ? (type as ResourceType) : undefined;

  return {
    reference,
    display,
    identifier: normalizedIdentifier,
    type: normalizedType,
  };
}

/**
 * Upserts a reference into a map keyed by `reference`, backfilling metadata when needed.
 */
export function upsertReferenceByReference<T extends ResourceRef>(
  byReference: Map<string, T>,
  candidate: T,
): void {
  const existing = byReference.get(candidate.reference);
  if (!existing) {
    byReference.set(candidate.reference, candidate);
    return;
  }

  const shouldUpdateDisplay = !existing.display && candidate.display;
  const shouldUpdateIdentifier = !existing.identifier && candidate.identifier;
  const shouldUpdateType = !existing.type && candidate.type;

  if (!shouldUpdateDisplay && !shouldUpdateIdentifier && !shouldUpdateType) {
    return;
  }

  byReference.set(candidate.reference, {
    ...existing,
    display: shouldUpdateDisplay ? candidate.display : existing.display,
    identifier: shouldUpdateIdentifier ? candidate.identifier : existing.identifier,
    type: shouldUpdateType ? candidate.type : existing.type,
  });
}

interface ParsedAccountCompartments {
  accounts: ResourceRef[];
  legacyAccount: ResourceRef | undefined;
}

export class InvalidPracticeCompartmentError extends Error {
  readonly code = "INVALID_PRACTICE_COMPARTMENT" as const;

  constructor(
    message: string,
    readonly details: {
      organizationReferencesFromAccounts: string[];
      legacyOrganizationReference?: string | undefined;
    },
  ) {
    super(message);
    this.name = "InvalidPracticeCompartmentError";
  }
}

/**
 * Validates trusted Medplum account-compartment references.
 */
export function assertValidAccountCompartments({ meta }: { meta: MedplumMeta | undefined }): void {
  if (!meta) return;

  if (meta.accounts !== undefined && !Array.isArray(meta.accounts)) {
    throw new InvalidPracticeCompartmentError(
      "Invalid practice compartment: meta.accounts must be an array of Reference objects.",
      {
        organizationReferencesFromAccounts: [],
        legacyOrganizationReference: undefined,
      },
    );
  }

  if (Array.isArray(meta.accounts)) {
    const invalidIndices = meta.accounts
      .map((account, index) => ({
        index,
        isValid: toResourceRef({ ref: account }) !== undefined,
      }))
      .filter((entry) => !entry.isValid)
      .map((entry) => entry.index);

    if (invalidIndices.length > 0) {
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts contains invalid references at indices ${invalidIndices.join(
          ", ",
        )}.`,
        {
          organizationReferencesFromAccounts: [],
          legacyOrganizationReference: undefined,
        },
      );
    }
  }

  if (meta.account !== undefined && !toResourceRef({ ref: meta.account })) {
    throw new InvalidPracticeCompartmentError(
      "Invalid practice compartment: meta.account must be a Reference with a non-empty reference string.",
      {
        organizationReferencesFromAccounts: [],
        legacyOrganizationReference: undefined,
      },
    );
  }
}

function parseAccountCompartmentsFromMeta(
  meta: MedplumMeta | undefined,
): ParsedAccountCompartments {
  const accounts = Array.isArray(meta?.accounts)
    ? meta.accounts
        .map((account) => toResourceRef({ ref: account }))
        .filter((account): account is ResourceRef => account !== undefined)
    : [];

  const legacyAccount = toResourceRef({ ref: meta?.account });

  return { accounts, legacyAccount };
}

function getOrganizationAccountsFromAccounts(accounts: ResourceRef[]): ResourceRef[] {
  const organizationAccounts = accounts.filter((account) =>
    account.reference.startsWith("Organization/"),
  );

  const byReference = new Map<string, ResourceRef>();
  for (const account of organizationAccounts) {
    upsertReferenceByReference(byReference, account);
  }

  return Array.from(byReference.values());
}

/**
 * Returns the single practice `Organization` compartment from Medplum `meta.accounts` / `meta.account`.
 */
export function getPracticeOrganizationFromMeta(
  meta: MedplumMeta | undefined,
): ResourceRef | undefined {
  const { accounts, legacyAccount } = parseAccountCompartmentsFromMeta(meta);

  const organizationAccounts = getOrganizationAccountsFromAccounts(accounts);
  const legacyOrganizationAccount = legacyAccount?.reference.startsWith("Organization/")
    ? legacyAccount
    : undefined;

  if (organizationAccounts.length > 0) {
    if (organizationAccounts.length > 1) {
      const referencesList = organizationAccounts.map((a) => a.reference).join(", ");
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts contains multiple Organization references (${referencesList}). Expected exactly one.`,
        {
          organizationReferencesFromAccounts: organizationAccounts.map((a) => a.reference),
          legacyOrganizationReference: legacyOrganizationAccount?.reference,
        },
      );
    }

    const fromAccounts = organizationAccounts[0];
    if (
      legacyOrganizationAccount &&
      legacyOrganizationAccount.reference !== fromAccounts.reference
    ) {
      throw new InvalidPracticeCompartmentError(
        `Invalid practice compartment: meta.accounts (${fromAccounts.reference}) does not match legacy meta.account (${legacyOrganizationAccount.reference}).`,
        {
          organizationReferencesFromAccounts: [fromAccounts.reference],
          legacyOrganizationReference: legacyOrganizationAccount.reference,
        },
      );
    }

    return {
      reference: fromAccounts.reference,
      display: fromAccounts.display ?? legacyOrganizationAccount?.display,
    };
  }

  return legacyOrganizationAccount;
}
